import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Button, Card, Col, DatePicker, Descriptions, Form, InputNumber, Layout, Modal, Row, Space, Spin, Switch, Table, Tag, Typography, message, Select } from 'antd';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { canManageSalaryStructure, canReadSalaryStructure, isSuperAdmin } from '../utils/permissions';
import salaryStructureService from '../services/salaryStructureService';
import organizationService from '../services/organizationService';

const FORMULA_FIELDS = [
  { name: 'ctc', label: 'CTC' },
  { name: 'variablePay', label: 'Variable Pay' },
  { name: 'basic', label: 'Basic' },
  { name: 'hra', label: 'HRA' },
  { name: 'pf', label: 'PF' }
];

const CUSTOM_FIELDS = [
  { name: 'ta', label: 'TA' },
  { name: 'da', label: 'DA' },
  { name: 'medicalAllowance', label: 'Medical Allowance' },
  { name: 'conveyanceAllowance', label: 'Conveyance Allowance' },
  { name: 'bonus', label: 'Bonus' },
  { name: 'incentive', label: 'Incentive' },
  { name: 'nps', label: 'NPS' },
  { name: 'insurance', label: 'Insurance' },  
  { name: 'professionalTax', label: 'Professional Tax' },
  { name: 'specialAllowance', label: 'Special Allowance' }
];

const emptyValues = {
  ctc: 0,
  basic: 0,
  hra: 0,
  ta: 0,
  da: 0,
  medicalAllowance: 0,
  conveyanceAllowance: 0,
  specialAllowance: 0,
  bonus: 0,
  incentive: 0,
  variablePay: 0,
  pf: 0,
  nps: 0,
  effectFrom: null,
  effectTo: null,
  isActive: true
};

const formatCurrency = (value) => `Rs. ${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatFormulaValue = (value) => {
  const numericValue = Number(value || 0);

  if (numericValue > 0 && numericValue < 1) {
    return `${(numericValue * 100).toFixed(2)}%`;
  }

  return `${numericValue}`;
};

const parseFormulaValue = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  const hasPercent = value.includes('%');
  const normalized = value.replace(/%/g, '').replace(/[^\d.]/g, '');

  if (normalized === '') {
    return '';
  }

  const parsedValue = Number(normalized);
  return hasPercent ? parsedValue / 100 : parsedValue;
};

const roundAmount = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const resolvePercentOrAmount = (value, baseAmount) => {
  const numericValue = Number(value || 0);
  return numericValue > 0 && numericValue < 1 ? roundAmount(baseAmount * numericValue) : roundAmount(numericValue);
};

const resolvePreview = (values = {}) => {
  const ctc = roundAmount(values.ctc);
  const variablePay = resolvePercentOrAmount(values.variablePay, ctc);
  const remainingCtc = Math.max(roundAmount(ctc - variablePay), 0);
  const basic = resolvePercentOrAmount(values.basic, remainingCtc);
  const hra = resolvePercentOrAmount(values.hra, basic);
  const pf = resolvePercentOrAmount(values.pf, basic);
  const ta = roundAmount(values.ta);
  const da = roundAmount(values.da);
  const medicalAllowance = roundAmount(values.medicalAllowance);
  const conveyanceAllowance = roundAmount(values.conveyanceAllowance);
  const bonus = roundAmount(values.bonus);
  const incentive = roundAmount(values.incentive);
  const nps = roundAmount(values.nps);
  const specialAllowance = Math.max(
    roundAmount(remainingCtc - basic - hra - ta - da - medicalAllowance - conveyanceAllowance - bonus - incentive),
    0
  );
  const totalEarnings = roundAmount(variablePay + basic + hra + ta + da + medicalAllowance + conveyanceAllowance + bonus + incentive + specialAllowance);
  const totalDeductions = roundAmount(pf + nps);
  const netPay = roundAmount(totalEarnings - totalDeductions);

  return {
    ctc,
    variablePay,
    remainingCtc,
    basic,
    hra,
    pf,
    ta,
    da,
    medicalAllowance,
    conveyanceAllowance,
    bonus,
    incentive,
    nps,
    specialAllowance,
    totalEarnings,
    totalDeductions,
    netPay
  };
};

function SalaryStructure() {
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [activeStructure, setActiveStructure] = useState(null);
  const [editingStructure, setEditingStructure] = useState(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const { user } = useSelector((state) => state.auth);

  const canRead = canReadSalaryStructure(user);
  const canManage = canManageSalaryStructure(user);

  const effectiveOrganizationId = useMemo(
    () => (isSuperAdmin(user) ? (selectedOrganizationId || user?.organizationId) : user?.organizationId),
    [selectedOrganizationId, user]
  );

  // Load organizations for SuperAdmin
  useEffect(() => {
    const loadOrganizations = async () => {
      if (!isSuperAdmin(user)) {
        setSelectedOrganizationId(user?.organizationId);
        return;
      }

      try {
        const response = await organizationService.getAll(1, 1000);
        setOrganizations(response.data || []);
        if (!selectedOrganizationId) {
          setSelectedOrganizationId(user?.organizationId);
        }
      } catch (error) {
        console.error('Failed to load organizations', error);
      }
    };

    loadOrganizations();
  }, [user]);


  const loadSalaryStructures = async () => {
    try {
      setLoading(true);
      const [listResponse, currentResponse] = await Promise.all([
        salaryStructureService.getAll({ organizationId: effectiveOrganizationId }),
        salaryStructureService.getCurrent({ organizationId: effectiveOrganizationId })
      ]);

      setSalaryStructures(listResponse.data || []);
      setActiveStructure(currentResponse.data || null);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to load salary structures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canRead && effectiveOrganizationId) {
      loadSalaryStructures();
    }
  }, [canRead, effectiveOrganizationId]);

  const openCreateModal = () => {
    setEditingStructure(null);
    const defaultValues = {
      ...emptyValues,
      ...(isSuperAdmin(user) && { organizationId: effectiveOrganizationId })
    };
    form.setFieldsValue(defaultValues);
    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingStructure(record);
    form.setFieldsValue({
      ...emptyValues,
      ...record,
      effectFrom: record.effectFrom ? dayjs(record.effectFrom) : null,
      effectTo: record.effectTo ? dayjs(record.effectTo) : null
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      const payload = {
        ...values,
        organizationId: isSuperAdmin(user) ? effectiveOrganizationId : user?.organizationId,
        effectFrom: values.effectFrom ? values.effectFrom.format('YYYY-MM-DD') : null,
        effectTo: values.effectTo ? values.effectTo.format('YYYY-MM-DD') : null
      };

      if (editingStructure?.id) {
        await salaryStructureService.update(editingStructure.id, payload);
        message.success('Salary structure updated successfully');
      } else {
        await salaryStructureService.create(payload);
        message.success('Salary structure created successfully');
      }

      setIsModalOpen(false);
      form.resetFields();
      await loadSalaryStructures();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save salary structure');
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) {
    return (
      <Layout style={{ padding: '20px' }}>
        <Card>
          <p>You do not have permission to view salary structure.</p>
        </Card>
      </Layout>
    );
  }

  const preview = resolvePreview(formValues || emptyValues);

  const columns = [
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value) => value ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>
    },
    {
      title: 'Effective From',
      dataIndex: 'effectFrom',
      key: 'effectFrom',
      render: (value) => value ? dayjs(value).format('DD MMM YYYY') : '-'
    },
    {
      title: 'Effective To',
      dataIndex: 'effectTo',
      key: 'effectTo',
      render: (value) => value ? dayjs(value).format('DD MMM YYYY') : '-'
    },
    {
      title: 'CTC',
      key: 'ctc',
      render: (_, record) => formatCurrency(record.resolvedComponents?.ctc || record.ctc)
    },
    {
      title: 'Gross',
      key: 'gross',
      render: (_, record) => formatCurrency(record.resolvedComponents?.grossSalary || 0)
    },
    {
      title: 'Net',
      key: 'net',
      render: (_, record) => formatCurrency(record.resolvedComponents?.netSalary || 0)
    },
    {
      title: 'Updated By',
      key: 'updatedBy',
      render: (_, record) => record.updater ? `${record.updater.firstName} ${record.updater.lastName}` : '-'
    }
  ];

  if (canManage) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type="link" onClick={() => openEditModal(record)}>
          Edit
        </Button>
      )
    });
  }

  return (
    <Layout style={{ padding: '20px' }}>
      <h1>Salary Structure</h1>
      {!canManage && (
        <Alert type="info" message="This page is read-only for your role." style={{ marginBottom: 16 }} />
      )}

      {isSuperAdmin(user) && (
        <div style={{ marginBottom: '16px' }}>
          <Select
            value={effectiveOrganizationId}
            onChange={setSelectedOrganizationId}
            style={{ width: '300px' }}
            placeholder="Select Organization"
            options={organizations.map((org) => ({
              label: org.name,
              value: org.id
            }))}
          />
        </div>
      )}

      <Spin spinning={loading || saving}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card
              title="Active Pay Structure"
              extra={canManage ? <Button type="primary" onClick={openCreateModal}>Add Structure</Button> : null}
            >
              {activeStructure ? (
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Effective From">
                    {activeStructure.effectFrom ? dayjs(activeStructure.effectFrom).format('DD MMM YYYY') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Effective To">
                    {activeStructure.effectTo ? dayjs(activeStructure.effectTo).format('DD MMM YYYY') : 'Open-ended'}
                  </Descriptions.Item>
                  <Descriptions.Item label="CTC">
                    {formatCurrency(activeStructure.resolvedComponents?.ctc || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Variable Pay">
                    {formatCurrency(activeStructure.resolvedComponents?.earnings?.variablePay || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Basic">
                    {formatCurrency(activeStructure.resolvedComponents?.earnings?.basic || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="HRA">
                    {formatCurrency(activeStructure.resolvedComponents?.earnings?.hra || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Special Allowance">
                    {formatCurrency(activeStructure.resolvedComponents?.earnings?.specialAllowance || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Gross Earnings">
                    {formatCurrency(activeStructure.resolvedComponents?.grossSalary || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Deductions">
                    {formatCurrency(activeStructure.resolvedComponents?.totalDeductions || 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Net Pay">
                    {formatCurrency(activeStructure.resolvedComponents?.netSalary || 0)}
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <p>No active salary structure configured.</p>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card title="Salary Structure History">
              <Table
                columns={columns}
                dataSource={salaryStructures}
                rowKey="id"
                pagination={{ pageSize: 5 }}
              />
            </Card>
          </Col>
        </Row>
      </Spin>

      <Modal
        title={editingStructure ? 'Edit Salary Structure' : 'Create Salary Structure'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={980}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={emptyValues}
        >
          {isSuperAdmin(user) && (
            <Form.Item
              label="Organization"
              name="organizationId"
              rules={[{ required: true, message: 'Please select organization' }]}
            >
              <Select
                placeholder="Select organization"
                options={organizations.map((org) => ({
                  label: org.name,
                  value: org.id
                }))}
              />  
              <input type="hidden" />
            </Form.Item>
          )}

          <Card size="small" title="Percentage / Formula Fields" style={{ marginBottom: 16 }}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
              For `Variable Pay`, `Basic`, `HRA`, and `PF`, values between `0` and `1` are treated as percentages.
              Example: `0.10` becomes `10%`, `0.30` becomes `30%`. Values `1` or more are treated as fixed amounts.
            </Typography.Paragraph>
            <Row gutter={[16, 0]}>
              {FORMULA_FIELDS.map((field) => (
                <Col xs={24} md={12} lg={8} key={field.name}>
                  <Form.Item
                    label={field.label}
                    name={field.name}
                    rules={[
                      { required: true, message: `Please enter ${field.label.toLowerCase()}` },
                      () => ({
                        validator(_, value) {
                          if (value === undefined || value === null || value === '') {
                            return Promise.resolve();
                          }

                          if (Number(value) < 0) {
                            return Promise.reject(new Error(`${field.label} must be non-negative`));
                          }

                          return Promise.resolve();
                        }
                      })
                    ]}
                  >
                    <InputNumber
                      min={0}
                      precision={4}
                      style={{ width: '100%' }}
                      formatter={formatFormulaValue}
                      parser={parseFormulaValue}
                    />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Card>

          <Card size="small" title="Custom Value Entries" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 0]}>
              {CUSTOM_FIELDS.map((field) => (
                <Col xs={24} md={12} lg={8} key={field.name}>
                  <Form.Item
                    label={field.label}
                    name={field.name}
                    rules={[{ required: true, message: `Please enter ${field.label.toLowerCase()}` }]}
                  >
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Card>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Effect From"
                name="effectFrom"
                rules={[{ required: true, message: 'Please select effect from date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Effect To" name="effectTo">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item label="Active Structure" name="isActive" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Card size="small" title="Preview">
            <Space direction="vertical">
              <span>CTC: {formatCurrency(preview.ctc)}</span>
              <span>Variable Pay: {formatCurrency(preview.variablePay)}</span>
              <span>Remaining CTC After Variable Pay: {formatCurrency(preview.remainingCtc)}</span>
              <span>Basic: {formatCurrency(preview.basic)}</span>
              <span>HRA: {formatCurrency(preview.hra)}</span>
              <span>PF: {formatCurrency(preview.pf)}</span>
              <span>Special Allowance: {formatCurrency(preview.specialAllowance)}</span>
              <span>Total Earnings: {formatCurrency(preview.totalEarnings)}</span>
              <span>Total Deductions: {formatCurrency(preview.totalDeductions)}</span>
              <span>Net Pay: {formatCurrency(preview.netPay)}</span>
            </Space>
          </Card>
        </Form>
      </Modal>
    </Layout>
  );
}

export default SalaryStructure;
